import { useTranslations } from "next-intl";
import { CheckboxWithValidation } from "@/components/Inputs/CheckboxWithValidation";
import { DISSOLVE_LOSSES_ACK_TEXT_KEY } from "@/lib/legal/dissolveLossesAck";

/** `body-3` line box in px — the label's first line, which the box centres on. */
const BODY_3_LINE_HEIGHT = 18;

interface LossesAckCheckboxProps {
  checked: boolean;
  onChangeAction: (checked: boolean) => void;
  /**
   * Id of the label — the locked Delete / Confirm names it (`aria-describedby`)
   * while the box is unticked, so a screen reader hears why it is off.
   */
  labelId: string;
  /** The request is running: the tick it carries is already captured. */
  disabled?: boolean;
}

/**
 * PB-CDP-7 — the required tick at the bottom of the amber losses notice of the
 * three dissolving dialogs (organisation delete, a solo owner's account
 * delete, the bell's company switch): "purchased event plans and balances
 * lapse, no refund". The label is read by the frozen key the acknowledgement
 * records (`DISSOLVE_LOSSES_ACK_TEXT_KEY`, version `dissolve-losses-ack-v1`),
 * so the key on the wire is the sentence on screen.
 *
 * Sits behind a divider in the notice; the notice's own text stays a
 * `role="note"` above it.
 */
export function LossesAckCheckbox({ checked, onChangeAction, labelId, disabled = false }: LossesAckCheckboxProps) {
  const tRoot = useTranslations();
  return (
    <div className="w-full border-t outline-status-amber pt-2.5 mt-2.5">
      <CheckboxWithValidation
        isChecked={checked}
        onCheckChangeAction={onChangeAction}
        size={16}
        alignStart
        labelLineHeight={BODY_3_LINE_HEIGHT}
        ariaRequired
        disabled={disabled}
        testId="dissolve-losses-ack"
      >
        <span id={labelId} className="block body-3-regular font-primary">
          {tRoot(DISSOLVE_LOSSES_ACK_TEXT_KEY)}
        </span>
      </CheckboxWithValidation>
    </div>
  );
}
